/* ─────────────────────────────────────────────
   PROVIDER
───────────────────────────────────────────── */
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from "react";

import {
  fetchCart,
  addToCartAPI,
  updateCartQtyAPI,
  removeCartItemAPI,
  clearCartAPI,
} from "../services/cartService";
const MAX_QTY = 20;

const FREE_SHIPPING_THRESHOLD = 499;

const SHIPPING_COST = 0;
const CartContext = createContext(null);
export function CartProvider({ children }) {

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  /* ─────────────────────────────────────────────
     LOAD CART
  ───────────────────────────────────────────── */
  const loadCart = useCallback(async () => {

    try {

      setLoading(true);

      const result = await fetchCart();
      console.log("Cart API result:", result); // ← add this
      if (result?.success) {

        const formattedItems = result?.data?.cart?.items?.map((item) => ({
          id: item._id,
          productId: item.product?._id,
          name: item.product?.name,
          price: item.product?.price,         // full price always
          displayPrice: item.price,           // actual charged price (discounted if offer)
          category: item.product?.category,
          rating: item.product?.rating,
          emoji: item.product?.images?.[0] || item.product?.image || "🌿",
          quantity: item.quantity,
          isFree: item.isFree || false,
          offerApplied: item.offerApplied || null,
          discountPercent: item.discountPercent || 0,
          originalPrice: item.originalPrice || item.product?.price,
        })) || [];

        setItems(formattedItems);
      }

      // In loadCart, after setting items, also store the backend discount:
      const discount = result?.data?.cart?.discount || 0;
      const coupon = result?.data?.cart?.coupon || null;
      // add these to state and expose in context value

    } catch (error) {

      console.error(
        "Load cart error:",
        error
      );

    } finally {

      setLoading(false);
    }

  }, []);

  /* ─────────────────────────────────────────────
     INITIAL LOAD
  ───────────────────────────────────────────── */
  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      loadCart();
    }
  }, [loadCart]);

  // Add this — refresh cart when user logs in
  useEffect(() => {
    const handleStorage = () => {
      const token = localStorage.getItem("accessToken");
      if (token) loadCart();
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [loadCart]);


  // Add this — refresh cart when user logs in
  useEffect(() => {
    const handleStorage = () => {
      const token = localStorage.getItem("accessToken");
      if (token) loadCart();
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [loadCart]);

  /* ─────────────────────────────────────────────
     ADD ITEM
  ───────────────────────────────────────────── */
  const addItem = useCallback(async (product) => {

    try {

      const result =
        await addToCartAPI(
          product._id || product.productId || product.id,
          product.quantity || 1
        );

      if (result?.success) {
        await loadCart();
      }

    } catch (error) {

      console.error(
        "Add item error:",
        error
      );
    }

  }, [loadCart]);

  /* ─────────────────────────────────────────────
     REMOVE ITEM
  ───────────────────────────────────────────── */
  const removeItem = useCallback(async (id) => {

    try {

      const result =
        await removeCartItemAPI(id);

      if (result?.success) {
        await loadCart();
      }

    } catch (error) {
      console.error(
        "Remove item error:",
        error
      );
    }
  }, [loadCart]);

  /* ─────────────────────────────────────────────
     UPDATE QTY
  ───────────────────────────────────────────── */
  const updateQty = useCallback(
    async (id, quantity) => {

      try {

        if (quantity < 1) {

          await removeCartItemAPI(id);

        } else {

          await updateCartQtyAPI(
            id,
            Math.min(quantity, MAX_QTY)
          );
        }

        await loadCart();

      } catch (error) {

        console.error(
          "Update qty error:",
          error
        );
      }

    },
    [loadCart]
  );

  /* ─────────────────────────────────────────────
     CLEAR CART
  ───────────────────────────────────────────── */
  const clearCart = useCallback(async () => {

    try {

      const result =
        await clearCartAPI();

      if (result?.success) {
        setItems([]);
      }

    } catch (error) {

      console.error(
        "Clear cart error:",
        error
      );
    }

  }, []);

  /* ─────────────────────────────────────────────
     DERIVED VALUES
  ───────────────────────────────────────────── */
  const derived = useMemo(() => {

    const itemCount =
      items.reduce(
        (sum, item) =>
          sum + item.quantity,
        0
      );

    const subtotal = items.reduce(
      (sum, item) => sum + (item.displayPrice ?? item.price) * item.quantity,
      0
    );



    const shipping =
      subtotal === 0
        ? 0
        : subtotal >= FREE_SHIPPING_THRESHOLD
          ? 0
          : SHIPPING_COST;

    const total =
      subtotal + shipping;

    const amountForFreeShipping =
      Math.max(
        0,
        FREE_SHIPPING_THRESHOLD -
        subtotal
      );

    return {
      itemCount,
      subtotal,
      shipping,
      total,
      amountForFreeShipping,
    };

  }, [items]);

  /* ─────────────────────────────────────────────
     CONTEXT VALUE
  ───────────────────────────────────────────── */
  const value = useMemo(
    () => ({

      // items
      items,

      // loading
      loading,

      // derived
      ...derived,

      // constants
      FREE_SHIPPING_THRESHOLD,
      SHIPPING_COST,
      MAX_QTY,

      // actions
      addItem,
      removeItem,
      updateQty,
      clearCart,

      // helper
      refreshCart: loadCart,

    }),
    [
      items,
      loading,
      derived,
      addItem,
      removeItem,
      updateQty,
      clearCart,
      loadCart,
    ]
  );

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

/* ─────────────────────────────────────────────
   HOOK
───────────────────────────────────────────── */
export function useCart() {

  const ctx = useContext(CartContext);

  if (!ctx) {
    throw new Error(
      "useCart must be used inside CartProvider"
    );
  }

  return ctx;
}

export default CartContext;